import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, GraduationCap, Menu, MessageSquare, TerminalSquare, X } from 'lucide-react';
import ThemeToggle from './ui/ThemeToggle';

interface HeaderProps {
  onOpenChat?: () => void;
  onOpenTerminal?: () => void;
}

interface LearnItem {
  key: string;
  title: string;
  subtitle: string;
  meta: string;
  externalUrl: string;
  externalLabel: string;
  internalPath: string;
  internalLabel: string;
}

const LEARN_ITEMS: LearnItem[] = [
  {
    key: 'scaffold',
    title: 'Scaffold',
    subtitle: 'AI context engineering education',
    meta: '8 Modules · 7 Labs · 15-Seat Cohorts',
    externalUrl: 'https://build.hiimjoe.com',
    externalLabel: 'Explore Scaffold ↗',
    internalPath: '/work/scaffold',
    internalLabel: 'See on Work page →',
  },
];

type SimpleNav = { kind: 'link'; label: string; to: string; match: RegExp };
type DropdownNav = { kind: 'dropdown'; label: string; items: LearnItem[]; activeMatch: RegExp };
type NavItem = SimpleNav | DropdownNav;

const NAV_ITEMS: NavItem[] = [
  { kind: 'link', label: 'work', to: '/work', match: /^\/work/ },
  {
    kind: 'dropdown',
    label: 'learn',
    items: LEARN_ITEMS,
    activeMatch: /^\/work\/scaffold/,
  },
  { kind: 'link', label: 'about', to: '/about', match: /^\/about/ },
  { kind: 'link', label: 'contact', to: '/contact', match: /^\/contact/ },
];

const LearnDropdown: React.FC<{
  label: string;
  items: LearnItem[];
  active: boolean;
}> = ({ label, items, active }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const scheduleClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  useEffect(() => () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
  }, []);

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`inline-flex items-center gap-1 px-s3 py-1.5 font-mono text-[11px] uppercase tracking-[0.6px] border-b transition-colors ${
          active
            ? 'text-accent border-accent'
            : 'text-text-muted border-transparent hover:text-text hover:border-accent-alt-hover'
        }`}
      >
        {label}
        <ChevronDown size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            role="menu"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            className="absolute left-0 top-full mt-2 w-[320px] rounded-md border border-border bg-bg-elev shadow-xl p-s4 z-50"
          >
            <div className="font-mono text-[10px] text-accent uppercase tracking-[1px] mb-s3 kicker-line">
              education
            </div>
            <div className="flex flex-col gap-s3">
              {items.map(item => (
                <div
                  key={item.key}
                  className="rounded-sm border border-border p-s3 flex gap-s3 bg-surface"
                >
                  <div className="shrink-0 mt-0.5">
                    <GraduationCap size={18} className="text-accent" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[14px] font-medium text-text tracking-[-0.2px]">
                      {item.title}
                    </div>
                    <div className="text-[12px] text-text-muted leading-snug mt-0.5">
                      {item.subtitle}
                    </div>
                    <div className="font-mono text-[10px] text-text-dim mt-1.5">
                      {item.meta}
                    </div>
                    <div className="flex flex-col gap-1.5 mt-s3">
                      <a
                        href={item.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-[11px] text-accent uppercase tracking-[0.6px] hover:underline hover:decoration-accent-alt-hover hover:underline-offset-2"
                      >
                        {item.externalLabel}
                      </a>
                      <Link
                        to={item.internalPath}
                        onClick={() => setOpen(false)}
                        className="font-mono text-[11px] text-text-muted uppercase tracking-[0.6px] hover:text-text"
                      >
                        {item.internalLabel}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Header: React.FC<HeaderProps> = ({ onOpenChat, onOpenTerminal }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileLearnOpen, setMobileLearnOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  // Translucent nav when the page is at the top; solid blurred bar once the
  // user scrolls past ~4px so the header separates from the hero visually.
  // Mobile menu open forces solid so the dropdown panel has a surface to
  // contrast against.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const chromeActive = scrolled || menuOpen;

  return (
    <header
      className={`sticky top-0 z-40 transition-[background-color,backdrop-filter,border-color] duration-200 ${
        chromeActive
          ? 'bg-bg/90 backdrop-blur-xl border-b border-border'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-content mx-auto flex items-center justify-between px-s6 md:px-s8 py-[14px]">
        <Link
          to="/"
          onClick={(e) => {
            // If we're already on "/", react-router treats this as a no-op —
            // pathname doesn't change, ScrollToTop's effect doesn't fire, and
            // the user stays where they are. Intercept and scroll manually.
            if (location.pathname === '/') {
              e.preventDefault();
              window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
              document.documentElement.scrollTop = 0;
              document.body.scrollTop = 0;
            }
          }}
          className="flex items-baseline gap-2.5 group"
        >
          <span
            className="font-hand text-[22px] text-accent leading-none tracking-[-0.02em] group-hover:opacity-90"
            style={{ paddingBottom: 2 }}
          >
            Joe
          </span>
          <span className="font-mono text-[10px] text-text-dim uppercase tracking-[1px] hidden sm:inline">
            / hiimjoe.com
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_ITEMS.map(item => {
            if (item.kind === 'dropdown') {
              const active = item.activeMatch.test(location.pathname);
              return (
                <LearnDropdown
                  key={item.label}
                  label={item.label}
                  items={item.items}
                  active={active}
                />
              );
            }
            const active = item.match.test(location.pathname);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`px-s3 py-1.5 font-mono text-[11px] uppercase tracking-[0.6px] border-b transition-colors ${
                  active
                    ? 'text-accent border-accent'
                    : 'text-text-muted border-transparent hover:text-text hover:border-accent-alt-hover'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-s3">
          <ThemeToggle compact />
          <button
            type="button"
            onClick={onOpenTerminal}
            className="p-2 rounded-xs border border-border text-text-muted hover:text-accent hover:border-accent transition-colors"
            title="Open terminal"
            aria-label="Open terminal"
          >
            <TerminalSquare size={14} />
          </button>
          <button
            type="button"
            onClick={onOpenChat}
            className="inline-flex items-center gap-2 px-s3 py-2 rounded-xs bg-accent text-accent-ink font-mono text-[11px] uppercase tracking-[0.4px] hover:brightness-110 transition"
          >
            <MessageSquare size={13} />
            ask joe
          </button>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen(o => !o)}
          className="md:hidden p-2 text-text-muted hover:text-text transition-colors"
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden overflow-hidden bg-bg border-t border-border"
          >
            <nav className="flex flex-col px-s6 py-s3">
              {NAV_ITEMS.map(item => {
                if (item.kind === 'dropdown') {
                  const active = item.activeMatch.test(location.pathname);
                  return (
                    <div key={item.label} className="border-b border-border last:border-b-0">
                      <button
                        type="button"
                        onClick={() => setMobileLearnOpen(o => !o)}
                        aria-expanded={mobileLearnOpen}
                        className={`w-full flex items-center justify-between py-2.5 font-mono text-[13px] uppercase tracking-[0.6px] ${
                          active ? 'text-accent' : 'text-text-muted'
                        }`}
                      >
                        <span>{item.label}</span>
                        <ChevronDown
                          size={14}
                          className={`transition-transform ${mobileLearnOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                      <AnimatePresence>
                        {mobileLearnOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="font-mono text-[10px] text-accent uppercase tracking-[1px] pt-s1 pb-s2 kicker-line">
                              education
                            </div>
                            <div className="flex flex-col gap-s3 pb-s4">
                              {item.items.map(li => (
                                <div
                                  key={li.key}
                                  className="rounded-sm border border-border p-s3 flex gap-s3 bg-surface"
                                >
                                  <div className="shrink-0 mt-0.5">
                                    <GraduationCap size={18} className="text-accent" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="font-mono text-[14px] font-medium text-text tracking-[-0.2px]">
                                      {li.title}
                                    </div>
                                    <div className="text-[12px] text-text-muted leading-snug mt-0.5">
                                      {li.subtitle}
                                    </div>
                                    <div className="font-mono text-[10px] text-text-dim mt-1.5">
                                      {li.meta}
                                    </div>
                                    <div className="flex flex-col gap-1.5 mt-s3">
                                      <a
                                        href={li.externalUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-mono text-[11px] text-accent uppercase tracking-[0.6px]"
                                      >
                                        {li.externalLabel}
                                      </a>
                                      <Link
                                        to={li.internalPath}
                                        className="font-mono text-[11px] text-text-muted uppercase tracking-[0.6px]"
                                      >
                                        {li.internalLabel}
                                      </Link>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }
                const active = item.match.test(location.pathname);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`py-2.5 font-mono text-[13px] uppercase tracking-[0.6px] border-b border-border last:border-b-0 ${
                      active ? 'text-accent' : 'text-text-muted'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <div className="flex items-center gap-s3 pt-s3 border-t border-border mt-s2">
                <ThemeToggle compact />
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenTerminal?.();
                  }}
                  className="p-2 rounded-xs border border-border text-text-muted"
                  aria-label="Open terminal"
                >
                  <TerminalSquare size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenChat?.();
                  }}
                  className="ml-auto inline-flex items-center gap-2 px-s3 py-2 rounded-xs bg-accent text-accent-ink font-mono text-[11px] uppercase tracking-[0.4px]"
                >
                  <MessageSquare size={13} />
                  ask joe
                </button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
